import React, { useState, useEffect } from 'react';
import { HiArrowPath } from 'react-icons/hi2';
import { Modal, ModalHeader, ModalBody } from '../ui/Modal';
import { VBButton } from '../ui/VBButton';
import * as serverOrgs from '../../../api/server/orgs';
import { ENTITY_STATUS } from '../../constants';
import { useToast } from '../../components/ui/Toast';
import { logger } from '../../../utils/logger';

interface DeactivatedOrgsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReactivated: (orgId: string) => void | Promise<void>;
}

export const DeactivatedOrgsModal: React.FC<DeactivatedOrgsModalProps> = ({
  isOpen,
  onClose,
  onReactivated
}) => {
  const [inactiveOrgs, setInactiveOrgs] = useState<serverOrgs.Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { addToast } = useToast();

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadInactiveOrgs = async () => {
      setIsLoading(true);
      setError('');
      try {
        const allOrgs = await serverOrgs.listOrgs({ includeInactive: true });
        if (!cancelled) {
          setInactiveOrgs(allOrgs.filter((org) => Number(org.status) === ENTITY_STATUS.INACTIVE));
        }
      } catch (err) {
        logger.error('[DeactivatedOrgsModal] Failed to load orgs:', err);
        if (!cancelled) {
          setError('Failed to load deactivated organizations');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadInactiveOrgs();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleReactivate = async (orgId: string) => {
    setReactivatingId(orgId);
    try {
      await serverOrgs.restoreOrg(orgId);
      logger.info('[DeactivatedOrgsModal] Organization reactivated:', orgId);
      addToast({
        type: 'success',
        title: 'Organization reactivated',
        message: 'You can manage it in Org Admin settings.'
      });
      setInactiveOrgs((prev) => prev.filter((org) => org.org_id !== orgId));
      await onReactivated(orgId);
    } catch (err) {
      logger.error('[DeactivatedOrgsModal] Failed to reactivate org:', err);
      setError('Failed to reactivate organization');
      addToast({ type: 'error', title: 'Failed to reactivate organization' });
    } finally {
      setReactivatingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Deactivated Organizations</ModalHeader>
      <ModalBody>
        {error && (
          <div className="mb-4 p-3 bg-vb-danger/10 border border-vb-danger/30 rounded-lg text-vb-danger text-sm dark:bg-vb-danger/20 dark:border-vb-danger/40">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="py-8 text-center text-vb-text-secondary dark:text-vb-text-secondary-dark text-sm">
            Loading...
          </div>
        ) : inactiveOrgs.length === 0 ? (
          <div className="py-8 text-center text-vb-text-secondary dark:text-vb-text-secondary-dark text-sm">
            No deactivated organizations
          </div>
        ) : (
          <div className="space-y-2">
            {inactiveOrgs.map((org) => (
              <div
                key={org.org_id}
                className="flex items-center justify-between p-3 rounded-lg bg-vb-bg-secondary dark:bg-vb-bg-secondary-dark"
              >
                <span className="text-sm text-vb-text-primary dark:text-vb-text-primary-dark truncate mr-3">
                  {org.name}
                </span>
                <VBButton
                  variant="secondary"
                  size="sm"
                  onClick={() => handleReactivate(org.org_id)}
                  disabled={reactivatingId !== null}
                  icon={<HiArrowPath className="w-4 h-4" />}
                  iconPosition="left"
                >
                  {reactivatingId === org.org_id ? 'Reactivating...' : 'Reactivate'}
                </VBButton>
              </div>
            ))}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
